import {
  Component,
  OnInit,
  signal,
  inject,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  DestroyRef
} from '@angular/core';
import {
  FeaturesEnabled,
  MenuDataService
} from '@core/services/application/menu-data.service';
import { MENU_DATA_SERVICE } from '@core/services';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { DrawerStateService } from '@core/services/drawer-state.service';
import { MenuItem } from 'primeng/api';
import { PanelMenuModule, PanelMenu } from 'primeng/panelmenu';
import { PanelMenuPassThroughOptions } from 'primeng/types/panelmenu';
import { filter, tap } from 'rxjs/operators';
import { NounPipe } from '@shared/pipes/nouns/noun.pipe';
import { AuthData } from '@core/resolvers/auth-data/auth-data.resolver';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Bind } from 'primeng/bind';

@Component({
  selector: 'app-menu',
  templateUrl: './app.menu.component.html',
  styleUrls: ['./app.menu.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [Bind, PanelMenu]
})
export class AppMenuComponent implements OnInit {
  private drawerStateService = inject(DrawerStateService);
  private menuDataService = inject(MENU_DATA_SERVICE);
  private nounPipe = inject(NounPipe);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  // Signals for reactive state
  protected menuItems = signal<MenuItem[]>([]);
  protected authData = signal<AuthData>(null);
  protected currentUrl = signal<string>('');

  // Expose drawer state signals
  protected isMobile = this.drawerStateService.isMobile;
  protected sidebarExpanded = this.drawerStateService.expanded;

  // PassThrough configuration for PanelMenu (PrimeNG 21)
  // Collapsed/expanded visual behavior is handled via CSS using .sidebar-collapsed parent class
  protected readonly panelMenuPT: PanelMenuPassThroughOptions = {
    root: {
      class: 'border-none bg-transparent p-0 w-full'
    },
    panel: {
      class: 'mb-0 border-none'
    },
    header: {
      class: 'border-none bg-transparent m-0 p-0'
    },
    headerContent: {
      class: 'border-none bg-transparent'
    },
    headerLink: {
      class: [
        'flex items-center gap-3 px-4 py-3 w-full',
        'transition-all duration-200 cursor-pointer',
        'border-none bg-transparent no-underline',
        'text-inherit'
      ]
    },
    headerIcon: {
      class: 'flex-shrink-0 order-3 text-[1rem]'
    },
    headerLabel: {
      class: 'flex-1 order-1'
    },
    submenuIcon: {
      class: 'order-2 ml-1 flex-shrink-0'
    },
    contentContainer: {
      class: 'border-none'
    },
    content: {
      class: 'p-0 m-0'
    },
    item: {
      class: 'm-0'
    },
    itemLink: {
      class: [
        'flex items-center gap-3 py-2.5 px-4 pl-12 w-full',
        'transition-all duration-200 no-underline',
        'border-none bg-transparent',
        'text-inherit'
      ]
    },
    itemIcon: {
      class: 'flex-shrink-0 order-2 text-[1rem]'
    },
    itemLabel: {
      class: 'flex-1 order-1'
    }
  };

  ngOnInit(): void {
    // Load auth data from route
    const auth = this.route.snapshot.data?.auth;
    this.authData.set(auth);

    // Get enabled pages and features
    const enabledPages = this.route.snapshot.data['enabledPages'];
    const enabledFeatures: FeaturesEnabled[] = auth?.enabledFeatures || [];

    // Existing feature flags
    enabledFeatures.push(['ai_notes_on', enabledPages?.ai_notes_on]);
    enabledFeatures.push([
      'parachute_enabled',
      enabledPages?.parachute_enabled,
      true
    ]);
    enabledFeatures?.push([
      'pickup_delivery_appointment_automation',
      enabledPages?.pickup_delivery_appointment_automation,
      true
    ]);

    // System settings flags (field names match SystemSettingFactory.findBySectionAndField keys)
    enabledFeatures.push(['hide_device_type', enabledPages?.hide_device_type]);

    // Set super admin flag and get menu list
    this.menuDataService.superAdmin = auth?.isSuperAdmin;
    const menuItems = this.menuDataService.getMenuList(enabledFeatures);

    // Apply noun substitutions (e.g. prescription_noun → Rx/Prescription)
    this.applyNounTransforms(menuItems);

    // Add command to close drawer on mobile when menu item is clicked
    this.addNavigationCommands(menuItems);

    this.menuItems.set(menuItems);

    // Track current URL for active route highlighting
    this.currentUrl.set(this.router.url);

    // Subscribe to Angular router navigation events to update active route
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        tap((event: NavigationEnd) => {
          this.currentUrl.set(event.urlAfterRedirects);
          this.updateActiveStates();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    // Subscribe to hash changes so v1 hash-based items get active-route highlighting
    const hashChangeHandler = () => {
      this.updateActiveStates();
    };
    window.addEventListener('hashchange', hashChangeHandler);
    this.destroyRef.onDestroy(() => {
      window.removeEventListener('hashchange', hashChangeHandler);
    });

    // Initial active state update
    this.updateActiveStates();
  }

  /**
   * Recursively apply noun transforms to menu item labels
   */
  private applyNounTransforms(items: MenuItem[]): void {
    items.forEach((item) => {
      if (item.label) {
        item.label = this.nounPipe.transform(item.label);
      }
      if (item.items?.length) {
        this.applyNounTransforms(item.items);
      }
    });
  }

  /**
   * Recursively add navigation commands to menu items.
   * Closes drawer on mobile when menu item is clicked (both routerLink and url items).
   */
  private addNavigationCommands(items: MenuItem[]): void {
    items.forEach((item) => {
      // Add command to handle navigation and drawer closing
      const originalCommand = item.command;
      item.command = (event) => {
        // Execute original command if exists
        if (originalCommand) {
          originalCommand(event);
        }

        // Close drawer on mobile for both routerLink and url-based items
        if (this.isMobile() && (item.routerLink || item.url)) {
          this.drawerStateService.close();
        }
      };

      // Recursively process child items
      if (item.items && item.items.length > 0) {
        this.addNavigationCommands(item.items);
      }
    });
  }

  /**
   * Update active states for menu items based on current route
   */
  private updateActiveStates(): void {
    const currentUrl = this.currentUrl();
    const menuItems = this.menuItems();

    this.markActiveItems(menuItems, currentUrl);

    // Trigger change detection by creating new array reference
    this.menuItems.set([...menuItems]);
  }

  /**
   * Recursively mark menu items as active based on current URL or current hash.
   * Also sets item.expanded so only sections containing the active route are open.
   */
  private markActiveItems(items: MenuItem[], currentUrl: string): boolean {
    let hasActiveChild = false;
    const currentHash = window.location.hash;

    items.forEach((item) => {
      // Reset active styleClass (strip previous active-route marker)
      item.styleClass = (item.styleClass ?? '')
        .replace(/\bactive-route\b/g, '')
        .trim();

      if (item.items && item.items.length > 0) {
        const childIsActive = this.markActiveItems(item.items, currentUrl);
        // Only expand this section if a child route matches
        item.expanded = childIsActive;
        if (childIsActive) {
          hasActiveChild = true;
          item.styleClass = `${item.styleClass} active-route`.trimStart();
        }
      }

      if (item.routerLink) {
        const routerLink = Array.isArray(item.routerLink)
          ? item.routerLink.join('/')
          : item.routerLink;

        if (
          currentUrl === routerLink ||
          currentUrl.startsWith(routerLink + '/')
        ) {
          item.styleClass = `${item.styleClass} active-route`.trimStart();
          hasActiveChild = true;
        }
      }

      if (item.url && currentHash && currentHash === item.url) {
        item.styleClass = `${item.styleClass} active-route`.trimStart();
        hasActiveChild = true;
      }
    });

    return hasActiveChild;
  }
}
